import cx from 'classnames';
import { Formik, useFormikContext } from 'formik';
import * as Yup from 'yup';

import { CheckboxField } from 'components/fields/checkbox-field';
import { SelectTagsField } from 'components/fields/select-fields-tags/select-fields-tags';
import { useTranslation } from 'react-i18next';

import { ReactComponent as CloseIcon } from 'assets/images/close.svg';
import { Button } from 'components/ui/Button';

import { DatePickerField, InputField, Checkbox } from 'components/common';
import { SelectField } from 'components/fields/select-fields/select-filed';
import React, { useMemo } from 'react';
import s from './filter.module.scss';

export type Option = { label: string; value: string };

interface FilterBase {
  field: { label: string; value: string };
}

export type Filter =
  | (FilterBase & {
      type: 'select' | 'select-multi' | 'date' | 'date-range' | 'input';
      options?: Option[];
    })
  | (FilterBase & {
      type: 'checkbox-list';
      options: Option[];
    });

const SelectAllCheckboxList = ({
  fieldName,
  options,
  label,
  t,
}: {
  fieldName: string;
  options: Option[];
  label?: string;
  t: ReturnType<typeof useTranslation>['t'];
}) => {
  const { values, setFieldValue } = useFormikContext<any>();

  const currentMap: Record<string, boolean> =
    (values?.[fieldName] as Record<string, boolean>) ?? {};

  const total = options.length;
  const selectedCount = options.reduce((acc, opt) => acc + (currentMap[opt.value] ? 1 : 0), 0);

  const allSelected = total > 0 && selectedCount === total;
  const someSelected = selectedCount > 0 && selectedCount < total;

  const toggleAll = () => {
    const newMap = options.reduce<Record<string, boolean>>((m, opt) => {
      m[opt.value] = !allSelected;
      return m;
    }, {});
    setFieldValue(fieldName, newMap, true);
  };

  return (
    <div className={s['checkbox-list-wrapper']}>
      <div className={s['checkbox-list-select-all']}>
        <Checkbox
          checked={allSelected}
          label={t('Select all')}
          onClick={toggleAll}
          aria-checked={allSelected ? 'true' : someSelected ? 'mixed' : 'false'}
        />
      </div>

      <div className={s['checkbox-list-options']}>
        {options.map((opt) => (
          <div key={opt.value} className={s['checkbox-list-option']}>
            <CheckboxField name={`${fieldName}.${opt.value}`} label={opt.label} />
          </div>
        ))}
      </div>
    </div>
  );
};

export const AsideFilter = ({
  isOpen,
  onSubmitForm,
  onClose,
  filters,
  isDetails = false,
  title,
  initialValuesOverride,
  validationSchemaOverride,
}: {
  filters: Filter[];
  isOpen: boolean;
  onSubmitForm: (values: any) => void;
  onClose: () => void;
  isDetails?: boolean;
  title?: string;
  initialValuesOverride?: Record<string, any>;
  validationSchemaOverride?: Yup.AnySchema;
}) => {
  const { t } = useTranslation();

  const onSubmit = (values: any) => {
    onSubmitForm?.(values);
    onClose?.();
  };

  const initialValues = useMemo(() => {
    if (initialValuesOverride) return initialValuesOverride;

    return (filters ?? []).reduce<Record<string, any>>((acc, filter) => {
      switch (filter.type) {
        case 'select':
          acc[filter.field.value] = undefined;
          break;
        case 'select-multi':
          acc[filter.field.value] = [];
          break;
        case 'date':
        case 'date-range':
          acc[filter.field.value] = undefined;
          break;
        case 'input':
          acc[filter.field.value] = '';
          break;
        case 'checkbox-list':
          acc[filter.field.value] = (filter.options ?? []).reduce<Record<string, boolean>>(
            (m, opt) => {
              m[opt.value] = false;
              return m;
            },
            {},
          );
          break;
        default:
          acc[(filter as any).field?.value ?? ''] = undefined;
      }
      return acc;
    }, {});
  }, [filters, initialValuesOverride]);

  const validationSchema = validationSchemaOverride ?? Yup.object().shape({});

  const Form = useMemo(() => {
    // eslint-disable-next-line react/display-name
    return () => (
      <React.Fragment>
        {filters?.map((filter) => {
          switch (filter.type) {
            case 'select':
              return (
                <SelectField
                  label={filter.field.label}
                  name={filter.field.value}
                  key={filter.field.value}
                  options={filter.options}
                  isMulti={false}
                />
              );
            case 'select-multi':
              return (
                <SelectTagsField
                  label={filter.field.label}
                  name={filter.field.value}
                  key={filter.field.value}
                  options={filter.options}
                  isMulti={true}
                />
              );
            case 'date':
              return (
                <DatePickerField
                  label={filter.field.label}
                  name={filter.field.value}
                  key={filter.field.value}
                />
              );
            case 'date-range':
              return (
                <DatePickerField
                  label={filter.field.label}
                  name={filter.field.value}
                  key={filter.field.value}
                  mode="range"
                />
              );
            case 'input':
              return (
                <InputField
                  label={filter.field.label}
                  name={filter.field.value}
                  key={filter.field.value}
                />
              );
            case 'checkbox-list':
              return (
                <div key={filter.field.value} className={s['checkbox-list']}>
                  <div className={s['checkbox-list-label']}>{filter.field.label}</div>

                  <SelectAllCheckboxList
                    fieldName={filter.field.value}
                    options={filter.options ?? []}
                    label={filter.field.label}
                    t={t}
                  />
                </div>
              );
            default:
              return <></>;
          }
        })}
      </React.Fragment>
    );
  }, [filters, t]);

  return (
    <Formik
      onSubmit={onSubmit}
      initialValues={initialValues}
      validationSchema={validationSchema}
    >
      {({ dirty, isSubmitting, values, submitForm, setFieldValue, resetForm }) => (
        <div className={cx(s['container'], { [s['open']]: isOpen })}>
          <div className={cx(s['wrapper'], { [s['open']]: isOpen })}>
            <div className={s['header']}>
              <span className={s['title']}>{title ?? t(isDetails ? 'Edit' : 'Filter')}</span>

              <div role="button" tabIndex={0} className={s['close']} onClick={onClose}>
                <CloseIcon />
              </div>
            </div>

            <div className={s['main']}>
              <Form />
            </div>

            <div className={s['footer']}>
              <Button
                className={cx(s['button'], s['button-clear'])}
                onClick={() => {
                  const resetValues = { ...initialValues };

                  filters?.forEach((filter) => {
                    if (filter.type === 'checkbox-list') {
                      resetValues[filter.field.value] = (filter.options ?? []).reduce<
                        Record<string, boolean>
                      >((m, opt) => {
                        m[opt.value] = false;
                        return m;
                      }, {});
                    }
                  });

                  resetForm({ values: resetValues });
                }}
              >
                {t('Clear')}
              </Button>

              <div className={s['right-group-btn']}>
                <Button className={cx(s['button'], s['button-back'])} onClick={onClose}>
                  {t('Back')}
                </Button>
                <Button className={cx(s['button'], s['button-save'])} onClick={submitForm}>
                  {t('Save')}
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </Formik>
  );
};
